import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
  Autocomplete,
} from '@mui/material';
import { AlertCircle, Check, GitBranch, Key, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectSettings } from '@/app/project-settings/layout/ProjectSettingsLayout';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { encryptString, fetchEntitlements } from '@/lib/crypto';
import {
  PlatformTable,
  DeleteConfirmDialog,
  GitAccessDialog,
} from './components';
import type { DataSourceItem } from './components';

export default function DataSourcesPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();
  const { projectName } = useProjectSettings();
  const { refreshProject } = useProjectName();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSources, setDataSources] = useState<DataSourceItem[]>([]);
  const platform = 'Git'; // Git-only form; Jira/Confluence moved to MCP section
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Git form state
  const [url, setUrl] = useState('');
  const [branch, setBranch] = useState('');
  const [branchOptions, setBranchOptions] = useState<string[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const branchFetchedUrlRef = useRef<string>('');

  // Project tokens (Git PAT)
  const [projectTokens, setProjectTokens] = useState<{ platform: string; url: string; encryptedConfig?: string; organization?: string; mappings?: Array<{ repoName: string; projectKey: string }>; scope?: string }[]>([]);

  // Git auth mode
  const [gitAuthMode, setGitAuthMode] = useState<'pid' | 'pat'>('pat');
  const [gitPat, setGitPat] = useState('');
  const [gitPatSaving, setGitPatSaving] = useState(false);


  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: DataSourceItem | null;
  }>({ open: false, item: null });

  // Git access confirmation dialog
  const [accessDialog, setAccessDialog] = useState<{
    open: boolean;
    validating: boolean;
    error: string;
  }>({ open: false, validating: false, error: '' });


  useEffect(() => {
    fetchDataSources();
  }, [appId]);

  const fetchDataSources = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      // Load shared data sources (Git repos)
      const response = await sentinelNetwork.get(`/config/project/${appId}/data-sources`);
      const loadedDs: DataSourceItem[] = response.data.data || [];
      setDataSources(loadedDs);

      // Load project tokens (Git PAT)
      try {
        const tokenRes = await sentinelNetwork.get(`/config/project/${appId}/tokens`);
        const tokens = tokenRes.data?.data || [];
        setProjectTokens(tokens);

        // Set git auth mode based on whether a Git PAT token exists
        const gitToken = tokens.find((t: any) => t.platform === 'Git');
        if (gitToken) setGitAuthMode('pat');
      } catch {
        setProjectTokens([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch data sources:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateGitUrl = (value: string): boolean => {
    if (value.trim() === '') return false;
    const gitUrlRegex = /^https:\/\/.*\.git$/;
    return gitUrlRegex.test(value);
  };

  const handleDeleteProjectToken = async (platform: string) => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/tokens`, {
        data: { platform },
      });
      setSnackbar({ open: true, message: `${platform} token removed`, severity: 'success' });
      await fetchDataSources();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to remove ${platform} token`, severity: 'error' });
    }
  };

  const handleAddGit = () => {
    if (!validateGitUrl(url)) {
      setSnackbar({ open: true, message: 'Invalid Git URL. Must be HTTPS and end with .git', severity: 'error' });
      return;
    }
    if (gitAuthMode === 'pat' && !projectTokens.find(t => t.platform === 'Git')) {
      setSnackbar({ open: true, message: 'Please save a Personal Access Token (PAT) before adding repositories', severity: 'error' });
      return;
    }
    setAccessDialog({ open: true, validating: false, error: '' });
  };

  const doAddGitDataSource = async () => {
    setSaving(true);
    try {
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, {
        platform: 'Git',
        url,
        branch: branch || 'main',
      });
      setSnackbar({ open: true, message: 'Git repository added successfully', severity: 'success' });
      await fetchDataSources();
      if (appId) await refreshProject(appId);
      setUrl('');
      setBranch('');
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to add data source', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAccessConfirm = async () => {
    setAccessDialog((prev) => ({ ...prev, validating: true, error: '' }));
    try {
      // Build validation payload — include encryptedToken when PAT mode
      const payload: Record<string, any> = { gitUrl: url, branches: [branch || 'main'] };
      if (gitAuthMode === 'pat') {
        const gitToken = projectTokens.find(t => t.platform === 'Git');
        if (gitToken?.encryptedConfig) {
          payload.encryptedToken = gitToken.encryptedConfig;
        }
      }
      const res = await sentinelNetwork.post('/validate-git-access', payload);
      const result = res.data;
      if (result.success) {
        setAccessDialog({ open: false, validating: false, error: '' });
        await doAddGitDataSource();
      } else {
        const fallbackError = gitAuthMode === 'pat'
          ? 'Access validation failed. Please ensure your Personal Access Token has Read access to this repository.'
          : 'Access validation failed. Please grant at least Read access to PID user PIDF6B7 before continuing.';
        setAccessDialog((prev) => ({
          ...prev,
          validating: false,
          error: result.message || fallbackError,
        }));
      }
    } catch (err: any) {
      // If validate-git-access endpoint doesn't exist, just proceed
      setAccessDialog({ open: false, validating: false, error: '' });
      await doAddGitDataSource();
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/data-sources`, {
        data: { url: deleteDialog.item.url },
      });

      await fetchDataSources();
      if (appId) await refreshProject(appId);
      setSnackbar({
        open: true,
        message: 'Data source removed successfully',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Failed to remove data source',
        severity: 'error',
      });
    } finally {
      setDeleteDialog({ open: false, item: null });
    }
  };

  const handleUpdateRepoMeta = async (item: DataSourceItem, field: string, value: any) => {
    // Update local state immediately
    setDataSources(prev =>
      prev.map(ds => ds.url === item.url && ds.platform === item.platform ? { ...ds, [field]: value } : ds)
    );
    // Persist to backend
    try {
      await sentinelNetwork.patch(`/config/project/${appId}/data-sources`, {
        url: item.url,
        [field]: value,
      });
    } catch (err: any) {
      console.error('Failed to update repo metadata:', err);
    }
  };

  const fetchBranches = useCallback(async (repoUrl: string) => {
    if (!repoUrl || !validateGitUrl(repoUrl)) { setBranchOptions([]); return; }
    const gitToken = projectTokens.find(t => t.platform === 'Git');
    if (!gitToken?.encryptedConfig) { setBranchOptions([]); return; }
    if (branchFetchedUrlRef.current === repoUrl) return;
    branchFetchedUrlRef.current = repoUrl;
    setBranchesLoading(true);
    try {
      const { data } = await sentinelNetwork.post<string[]>('/config/onboarding-secops/validate/ghe/branches', {
        token: gitToken.encryptedConfig,
        repoUrl,
      });
      setBranchOptions(Array.isArray(data) ? data : []);
    } catch {
      setBranchOptions([]);
    } finally {
      setBranchesLoading(false);
    }
  }, [projectTokens]);

  const getAddedUrlsForPlatform = (p: string) =>
    dataSources.filter((ds) => ds.platform === p).map((ds) => ds.url);

  const filterByPlatform = (p: string) =>
    dataSources.filter((ds) => ds.platform === p);

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <GitBranch size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              Repositories
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage Git repositories for this project
          </Typography>
        </Box>

        {/* GitHub Integration */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>GitHub Integration</Typography>

            {/* Auth mode selection — PID option hidden for now */}
            <FormControl size="small" sx={{ mb: 2, display: 'none' }}>
              <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Authentication method</FormLabel>
              <RadioGroup row value={gitAuthMode} onChange={(e) => setGitAuthMode(e.target.value as 'pid' | 'pat')}>
                <FormControlLabel value="pid" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>PID User Access (grant Sentinel read access)</Typography>} />
                <FormControlLabel value="pat" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Personal Access Token (PAT)</Typography>} />
              </RadioGroup>
            </FormControl>

            {/* PAT input */}
            {gitAuthMode === 'pat' && (
              <Box sx={{ mb: 2 }}>
                {projectTokens.find(t => t.platform === 'Git') ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip icon={<Check size={12} />} label="PAT configured" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />
                    <Button size="small" color="error" variant="text" startIcon={<Trash2 size={14} />} onClick={() => handleDeleteProjectToken('Git')}>
                      Remove
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Git Personal Access Token"
                      type="password"
                      placeholder="ghp_xxxx or your enterprise PAT"
                      value={gitPat}
                      onChange={(e) => setGitPat(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={gitPatSaving || !gitPat.trim()}
                      startIcon={gitPatSaving ? <CircularProgress size={14} color="inherit" /> : <Key size={14} />}
                      onClick={async () => {
                        setGitPatSaving(true);
                        try {
                          const { publicKey } = await fetchEntitlements();
                          const encryptedConfig = encryptString(gitPat, publicKey);
                          await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
                            platform: 'Git', encryptedConfig, url: 'git-pat', scope: 'shared',
                          });
                          setSnackbar({ open: true, message: 'Git PAT saved', severity: 'success' });
                          setGitPat('');
                          await fetchDataSources();
                        } catch (err: any) {
                          setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to save PAT', severity: 'error' });
                        } finally {
                          setGitPatSaving(false);
                        }
                      }}
                      sx={{ height: 40 }}
                    >
                      {gitPatSaving ? 'Saving...' : 'Save Token'}
                    </Button>
                  </Stack>
                )}
              </Box>
            )}

            {/* Add repo form */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Repository URL"
                  placeholder="https://github.com/org/repo.git"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); branchFetchedUrlRef.current = ''; setBranchOptions([]); }}
                  fullWidth
                  size="small"
                  required
                  error={url !== '' && !validateGitUrl(url)}
                  helperText={url !== '' && !validateGitUrl(url) ? 'Must be HTTPS and end with .git' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  freeSolo
                  options={branchOptions}
                  value={branch}
                  onInputChange={(_, value) => setBranch(value)}
                  onOpen={() => fetchBranches(url)}
                  loading={branchesLoading}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Branch"
                      placeholder="main (default)"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {branchesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Plus size={20} />}
                  onClick={handleAddGit}
                  disabled={saving || !url.trim() || !validateGitUrl(url)}
                  sx={{ height: 40, fontWeight: 600 }}
                >
                  {saving ? 'Adding...' : 'Add Repository'}
                </Button>
              </Grid>
            </Grid>

            {/* Repos table */}
            {filterByPlatform('Git').length > 0 && (
              <PlatformTable
                platformName="Git"
                items={filterByPlatform('Git')}
                onDelete={(item) => setDeleteDialog({ open: true, item })}
                onUpdateRepoMeta={handleUpdateRepoMeta}
              />
            )}

            {/* Access info — only shown for PID user mode */}
            {gitAuthMode === 'pid' && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<AlertCircle size={16} />}>
                Please grant PID user <strong>PIDF6B7</strong> of Sentinel a minimum of <strong>Read access</strong> to your repository.
                Write access is required only to create branches and raise Pull Requests.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        item={deleteDialog.item}
        onClose={() => setDeleteDialog({ open: false, item: null })}
        onConfirm={handleDelete}
      />

      {/* Git Access Confirmation Dialog */}
      <GitAccessDialog
        open={accessDialog.open}
        validating={accessDialog.validating}
        error={accessDialog.error}
        url={url}
        authMode={gitAuthMode}
        onClose={() => setAccessDialog({ open: false, validating: false, error: '' })}
        onConfirm={handleAccessConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
